import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { useAuth } from "../../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function EquipmentList({ items, onChange, label, tempDefault }) {
  const add = () => onChange([...items, { nome: "", ubicazione: "", temperatura_attesa: tempDefault }]);
  const update = (i, field, val) => {
    const next = [...items];
    next[i] = { ...next[i], [field]: val };
    onChange(next);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <Button type="button" variant="outline" size="sm" onClick={add}><Plus className="mr-1 h-3 w-3" />Aggiungi</Button>
      </div>
      {items.map((item, i) => (
        <div key={i} className="grid grid-cols-12 gap-2">
          <Input className="col-span-4" placeholder="Nome" value={item.nome || ""} onChange={(e) => update(i, "nome", e.target.value)} />
          <Input className="col-span-4" placeholder="Ubicazione" value={item.ubicazione || ""} onChange={(e) => update(i, "ubicazione", e.target.value)} />
          <Input className="col-span-3" placeholder="Temp." value={item.temperatura_attesa || ""} onChange={(e) => update(i, "temperatura_attesa", e.target.value)} />
          <Button type="button" variant="ghost" size="icon" className="col-span-1 text-red-500" onClick={() => remove(i)}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
    </div>
  );
}

export default function HaccpConfigDialog({
  open,
  onOpenChange,
  token,
  onSaved,
  apiBase = "/api/stampe-mensili",
}) {
  const { activeStruttura } = useAuth();
  const [frighi, setFrighi] = useState([]);
  const [congelatori, setCongelatori] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !token) return;
    setLoading(true);
    axios.get(`${API_URL}${apiBase}/config/haccp`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        setFrighi(r.data.frighi || []);
        setCongelatori(r.data.congelatori || []);
      })
      .finally(() => setLoading(false));
  }, [open, token, activeStruttura?.id, apiBase]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}${apiBase}/config/haccp`, { frighi, congelatori }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Configurazione HACCP salvata");
      onSaved?.();
      onOpenChange(false);
    } catch {
      toast.error("Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Configurazione HACCP</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <div className="space-y-6 py-2">
            <EquipmentList items={frighi} onChange={setFrighi} label="Frigo" tempDefault="4°C" />
            <EquipmentList items={congelatori} onChange={setCongelatori} label="Congelatori" tempDefault="-18°C" />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={save} disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
